import { API_ENDPOINTS } from '@/utils/constants/end-points';

import apiClient from '../api-client';

export interface SecurityOption {
  id: string | number;
  label: string;
}

export const fetchSecurities = async (): Promise<SecurityOption[]> => {
  const res = await apiClient.get(`${API_ENDPOINTS.SHARECREDIT}/securities`);
  return res?.data?.data;
};

export const fetchSignatories = async (
  securityId: number | string
): Promise<any[]> => {
  const res = await apiClient.get(
    `${API_ENDPOINTS.MASTER}/signatories?SecurityId=${securityId}`
  );
  return res?.data?.data || res?.data || [];
};

export interface CombinedSecurityEmails {
  securityId: string | number;
  emails: string[];
}

export const fetchCombinedSecurityEmails = async (
  securityId: number | string
): Promise<CombinedSecurityEmails> => {
  const res = await apiClient.get(
    `${API_ENDPOINTS.MASTER}/combined-security-emails?securityId=${securityId}`
  );
  // The endpoint wraps its result in the standard { success, data, message }
  // envelope — unwrap it, falling back to the bare body.
  return res?.data?.data || res?.data;
};

export const fetchReadyAwards = async (params: {
  securityId: number | string;
  search?: string;
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}) => {
  const { securityId, search, offset, limit, sortBy, sortDir } = params;

  const queryParams = new URLSearchParams();
  queryParams.append('SecurityId', String(securityId));
  if (search) {
    queryParams.append('Search', search);
  }
  if (offset !== undefined) queryParams.append('Offset', String(offset));
  if (limit !== undefined) queryParams.append('Limit', String(limit));
  if (sortBy) {
    queryParams.append('SortBy', sortBy);
    queryParams.append('SortDir', sortDir ?? 'asc');
  }

  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/ready-awards?${queryParams.toString()}`
  );

  const payload = res?.data?.data || res?.data || {};
  const items = Array.isArray(payload) ? payload : payload.items || [];

  return {
    items: items.map((item: any, idx: number) => ({
      ...item,
      id: item.id ?? `${item.awardNo}-${item.vestingDate}-${idx}`,
    })),
    totalCount: payload.totalCount ?? items.length,
    totalAvailableTreasuryShares:
      Number(payload.totalAvailableTreasuryShares) || 0,
  };
};

export const fetchBatchSummary = async (payload: {
  securityId: number | string;
  selectedAwards: { awardNo: string; vestingDate: string }[];
}) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS.SHARECREDIT}/batch-summary`,
    payload
  );
  return res?.data?.data || res?.data;
};

export const submitBatchVerification = async (payload: {
  batchId: number;
  verifiedBySignatoryId?: number;
  approvedBySignatoryIds?: number[];
  templateId?: number;
  latestIssuedShares?: number;
  latestPaidupCapital?: number;
}) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${payload?.batchId}/submission`,
    payload
  );
  return res?.data;
};

export const sendToShareRegistrar = async (
  batchNo: number | string,
  payload: { templateIds: number[] }
) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchNo}/registrar-submissions`,
    payload
  );
  return res?.data;
};

export const verifyShareRegistrar = async (
  batchNo: number | string,
  file: File | null
) => {
  const formData = new FormData();
  if (file) {
    formData.append('File', file);
  }

  const res = await apiClient.post(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchNo}/registrar-verifications`,
    formData,
    {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return res?.data;
};

export interface FetchExistingBatchesParams {
  securityId: number | string;
  offset?: number;
  limit?: number;
  searchField?: string;
  statusId?: number;
  vestingDateFrom?: string;
  vestingDateTo?: string;
  submitted?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const fetchExistingBatches = async (
  params: FetchExistingBatchesParams
) => {
  const {
    securityId,
    offset,
    limit,
    searchField,
    statusId,
    vestingDateFrom,
    vestingDateTo,
    submitted,
    sortBy,
    sortDir,
  } = params;

  const queryParams = new URLSearchParams();
  queryParams.append('securityId', String(securityId));
  if (offset !== undefined) queryParams.append('Offset', String(offset));
  if (limit !== undefined) queryParams.append('Limit', String(limit));
  if (searchField) queryParams.append('SearchField', searchField);
  if (statusId !== undefined) queryParams.append('StatusId', String(statusId));
  if (vestingDateFrom) queryParams.append('VestingDateFrom', vestingDateFrom);
  if (vestingDateTo) queryParams.append('VestingDateTo', vestingDateTo);
  if (submitted !== undefined)
    queryParams.append('Submitted', String(submitted));
  if (sortBy) {
    queryParams.append('SortBy', sortBy);
    queryParams.append('SortDir', sortDir ?? 'asc');
  }

  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/batches?${queryParams.toString()}`
  );
  return res?.data?.data || res?.data || [];
};

export interface FetchCheckerBatchesParams {
  offset?: number;
  limit?: number;
  searchField?: string;
  vestingDateFrom?: string;
  vestingDateTo?: string;
  workflowStatusId?: number | number[];
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const fetchCheckerBatches = async (
  params: FetchCheckerBatchesParams
) => {
  const {
    offset,
    limit,
    searchField,
    vestingDateFrom,
    vestingDateTo,
    sortBy,
    sortDir,
  } = params;

  const queryParams = new URLSearchParams();
  if (offset !== undefined) queryParams.append('Offset', String(offset));
  if (limit !== undefined) queryParams.append('Limit', String(limit));
  if (searchField) queryParams.append('SearchField', searchField);
  if (vestingDateFrom) queryParams.append('VestingDateFrom', vestingDateFrom);
  if (vestingDateTo) queryParams.append('VestingDateTo', vestingDateTo);
  if (sortBy) queryParams.append('SortBy', sortBy);
  if (sortDir) queryParams.append('SortDir', sortDir);

  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/checker/batches?${queryParams.toString()}`
  );
  return res?.data?.data || res?.data || [];
};

export interface GenerateBatchPayload {
  securityId: number;
  selectedAwards: {
    awardNo: string;
    vestingDate: string;
  }[];
  batchDate: string;
  shareTypeId: number;
  treasuryShares: number;
  wgtAvgTreasuryPrice: number | null;
  totalShares: number;
  lapseTenderedUsers: boolean;
}

// Returns the full response envelope ({ success, data, message }) because the
// caller needs both the new batch id (data) and the server message.
export const generateBatch = async (payload: GenerateBatchPayload) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS.SHARECREDIT}/batches`,
    payload
  );
  return res?.data;
};

export const fetchBatchDetails = async (batchNo: string | number) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchNo}`
  );
  return res?.data?.data || res?.data;
};

export const fetchBatchDetailsForView = async (batchNo: string | number) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchNo}/review`
  );
  return res?.data?.data || res?.data;
};

export const deleteShareCreditBatch = async (batchId: string | number) => {
  const res = await apiClient.delete(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchId}`
  );
  return res?.data;
};
export interface UpdateBatchDetailsPayload {
  batchDate: string;
  verifiedBySignatoryId: number;
  approvedBySignatoryIds: number[];
  /** The "Letter to SGX" is optional — `null` when none is selected. */
  templateId: number | null;
  clearTemplate: boolean;
}

export const updateBatchDetails = async (
  batchId: number | string,
  payload: UpdateBatchDetailsPayload
) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchId}/details`,
    payload
  );
  return res?.data?.data || res?.data;
};

export const handleBatchCheckerAction = async (
  batchId: number | string,
  payload: { action: 'A' | 'R'; checkerRemarks: string }
) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS.SHARECREDIT}/checker/batches/${batchId}/reviews`,
    payload
  );
  return res?.data;
};

export interface StakeholderNotificationRow {
  templateId: number;
  recipientEmails: string[];
  attachmentTemplateIds: number[];
}

export interface StakeholderNotificationsPayload {
  rows: StakeholderNotificationRow[];
  // True when the rows are empty — signals the backend to clear any previously
  // saved notifications for the batch.
  clearAll: boolean;
}

export const submitStakeholderNotifications = async (
  batchNo: number | string,
  payload: StakeholderNotificationsPayload
) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchNo}/stakeholder-notifications`,
    payload
  );
  return res?.data;
};

export const submitBatchCrediting = async (batchNo: number | string) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchNo}/crediting`
  );
  return res?.data;
};

export const restartBatch = async (batchId: number | string) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchId}/restart`
  );
  return res?.data;
};

export interface FetchCreditingDetailsParams {
  securityId?: number | string;
  searchField?: string;
  offset?: number;
  limit?: number;
  vestingDateFrom?: string;
  vestingDateTo?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const fetchCreditingDetails = async (
  params: FetchCreditingDetailsParams
) => {
  const {
    securityId,
    searchField,
    offset,
    limit,
    vestingDateFrom,
    vestingDateTo,
    sortBy,
    sortDir,
  } = params;

  const queryParams = new URLSearchParams();
  if (securityId !== undefined && securityId !== '')
    queryParams.append('SecurityId', String(securityId));
  if (searchField) queryParams.append('SearchField', searchField);
  if (offset !== undefined) queryParams.append('Offset', String(offset));
  if (limit !== undefined) queryParams.append('Limit', String(limit));
  if (vestingDateFrom) queryParams.append('VestingDateFrom', vestingDateFrom);
  if (vestingDateTo) queryParams.append('VestingDateTo', vestingDateTo);
  if (sortBy) {
    queryParams.append('SortBy', sortBy);
    queryParams.append('SortDir', sortDir ?? 'asc');
  }

  const res = await apiClient.get(
    `${API_ENDPOINTS.CREDITDETAILS}?${queryParams.toString()}`
  );
  // eslint-disable-next-line no-console
  console.log('Crediting Details Response:', res);
  return res?.data?.data || res?.data || [];
};

export const fetchCreditingStockPrices = async (params: {
  securityId: number | string;
  listingDate?: string;
}) => {
  const { securityId, listingDate } = params;

  const queryParams = new URLSearchParams();
  if (securityId !== undefined && securityId !== '')
    queryParams.append('SecurityId', String(securityId));
  if (listingDate) queryParams.append('ListingDate', listingDate);

  const res = await apiClient.get(
    `${API_ENDPOINTS.CREDITDETAILS}/stock-prices?${queryParams.toString()}`
  );
  return res?.data?.data || res?.data;
};

export interface CreditingSubmissionPayload {
  batchNos: number[];
  listingDate: string;
  listingPrice: number;
  sendEmail: boolean;
  emailType: number;
  creditingLetterTemplateId: number;
  coverLetterTemplateId: number;
}

export const submitCreditingDetails = async (
  payload: CreditingSubmissionPayload
) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS.CREDITDETAILS}/submissions`,
    payload
  );
  return res?.data;
};

export const downloadAllotmentSummaryTemplate = async (
  batchId: number | string,
  templateId: number | string,
  params?: {
    verifiedBySignatoryId?: number;
    approvedBySignatoryIds?: number[];
  }
) => {
  const queryParams = new URLSearchParams();
  if (params?.verifiedBySignatoryId !== undefined) {
    queryParams.append(
      'verifiedBySignatoryId',
      String(params.verifiedBySignatoryId)
    );
  }
  params?.approvedBySignatoryIds?.forEach(id => {
    queryParams.append('approvedBySignatoryIds', String(id));
  });
  const query = queryParams.toString();

  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchId}/allotment-summary/templates/${templateId}/download${
      query ? `?${query}` : ''
    }`,
    { responseType: 'blob' }
  );
  return res?.data;
};

// A2 — "Application for Listing (A2)" (templateCode PSPALA), only present when
// the batch's shareTypeName is "New Shares". Unlike the A1 summary, every query
// param here is required by the backend.
export const downloadAlaTemplate = async (
  batchId: number | string,
  templateId: number | string,
  params: {
    verifiedBySignatoryId: number;
    latestIssuedShares: number;
    latestPaidupCapital: number;
  }
) => {
  const queryParams = new URLSearchParams();
  queryParams.append(
    'verifiedBySignatoryId',
    String(params.verifiedBySignatoryId)
  );
  queryParams.append('latestIssuedShares', String(params.latestIssuedShares));
  queryParams.append('latestPaidupCapital', String(params.latestPaidupCapital));

  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchId}/ala/templates/${templateId}/download?${queryParams.toString()}`,
    { responseType: 'blob' }
  );
  return res?.data;
};

export const downloadCdpTextFile = async (batchId: number | string) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchId}/cdp-text-file/download`,
    { responseType: 'text' }
  );
  return res?.data;
};

export const downloadCdpFormTemplate = async (
  batchId: number | string,
  templateId: number | string
) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchId}/cdp-form/templates/${templateId}/download`,
    { responseType: 'blob' }
  );
  return res?.data;
};

export const downloadForm42Template = async (
  batchId: number | string,
  templateId: number | string
) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchId}/form42/templates/${templateId}/download`,
    { responseType: 'blob' }
  );
  return res?.data;
};

export const downloadSgxLetterTemplate = async (
  batchId: number | string,
  templateId: number | string
) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS.SHARECREDIT}/batches/${batchId}/sgx-letter/templates/${templateId}/download`,
    { responseType: 'blob' }
  );
  return res?.data;
};
